import { parseVanDerValkModuleLayout } from './applyVanDerValkSuitabilityResultToSystem'
import type { PitchedCladdingOptions } from './pitchedCladdingFieldConfig'
import type { VanDerValkPitchedCladdingType } from './pitchedCladdingFieldConfig'
import { SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS } from './suitabilityOptions'
import { resolveMaxCtcDistanceForBom } from './solarFixFieldUtils'
import type { VanDerValkSuitabilityConfig } from './types'
import { isVanDerValkSuitabilityConfigRow } from './types'

export type PitchedVendorFrame = Record<string, string | number | boolean | string[]>

const ROOF_HOOK_CLADDING_TYPES = new Set<VanDerValkPitchedCladdingType>(['TILED', 'SLATE', 'PLAIN', 'MEMBRANE'])

const DERIVED_ROOF_HOOK_TYPE: Partial<Record<VanDerValkPitchedCladdingType, string>> = {
  SLATE: 'SLATE',
  PLAIN: 'PLAIN',
  MEMBRANE: 'BITUMEN',
}

const ROOF_HOOK_SYSTEMS = new Set(['VALK_PITCHED', 'VALK_PITCHED_VALK_ACE'])

function parsePositiveNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10)
  if (!Number.isFinite(n) || n < 1) {
    return undefined
  }
  return n
}

/** True when roof construction height is allowed for the material + fastener pair. */
export function isValidSolarFixRoofConstructionHeight(
  roofMaterial: string,
  fastenerType: string,
  height: number
): boolean {
  const allowed = SOLAR_FIX_ROOF_CONSTRUCTION_HEIGHTS[roofMaterial]?.[fastenerType]
  return Array.isArray(allowed) && allowed.includes(height)
}

function parseProfileLength(value: unknown): number | undefined {
  return parsePositiveNumber(value)
}

function nonEmptyString(value: unknown): string {
  return value != null ? String(value).trim() : ''
}

/** Resolve effective roof-hook type from cladding and row (SLATE/PLAIN/BITUMEN derive from cladding). */
export function resolvePitchedRoofHookType(
  claddingType: VanDerValkPitchedCladdingType | null | undefined,
  rowRoofHookType: unknown
): string {
  if (claddingType) {
    const derived = DERIVED_ROOF_HOOK_TYPE[claddingType]
    if (derived) {
      return derived
    }
  }
  return nonEmptyString(rowRoofHookType)
}

/** Map suitability long profile code or numeric length to vendor ``longProfiles`` array. */
export function resolveLongProfiles(row: VanDerValkSuitabilityConfig): string[] {
  const code = nonEmptyString(row.longProfile)
  if (code) {
    return [code]
  }
  const length = parseProfileLength(row.profileLength)
  if (length != null) {
    return [`L${length}`]
  }
  return []
}

function baseOrientationAndColor(row: VanDerValkSuitabilityConfig): { orientation: string; color: string } | null {
  const layout = parseVanDerValkModuleLayout(row.moduleOrientation)
  const color = nonEmptyString(row.color)
  if (!color || (layout !== 'landscape' && layout !== 'portrait')) {
    return null
  }
  return {
    orientation: layout === 'portrait' ? 'PORTRAIT' : 'LANDSCAPE',
    color,
  }
}

function buildSimpleFixationFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string },
  fixation: string
): PitchedVendorFrame {
  return {
    orientation: base.orientation,
    color: base.color,
    fixation,
  }
}

function buildCorrugatedHangerBoltFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string }
): PitchedVendorFrame | null {
  const longProfiles = resolveLongProfiles(row)
  const hangerBoltOption = nonEmptyString(row.hangerBolt)
  if (!longProfiles.length || !hangerBoltOption) {
    return null
  }
  return {
    orientation: base.orientation,
    color: base.color,
    fixation: 'HANGER_BOLT',
    longProfiles,
    hangerBoltOption,
  }
}

function buildCorrugatedMountingBracketFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string }
): PitchedVendorFrame | null {
  const longProfiles = resolveLongProfiles(row)
  const mountingBracketBoltSize = nonEmptyString(row.mountingBracketBoltSize)
  if (!longProfiles.length || !mountingBracketBoltSize) {
    return null
  }
  return {
    orientation: 'LANDSCAPE',
    color: base.color,
    fixation: 'MOUNTING_BRACKET',
    longProfiles,
    mountingBracketBoltSize,
  }
}

function buildStandingSeamFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string }
): PitchedVendorFrame | null {
  const longProfiles = resolveLongProfiles(row)
  const seamClampType = nonEmptyString(row.seamClampType)
  if (!longProfiles.length || !seamClampType) {
    return null
  }
  return {
    orientation: base.orientation,
    color: base.color,
    longProfiles,
    seamClampType,
  }
}

export function screwTypeRequiredForRoofHook(roofHookType: string): boolean {
  const upper = roofHookType.toUpperCase()
  return upper !== 'SLIM' && upper !== 'SLIM_ADJ'
}

/** Screw options for the vendor BOM frame (from row metadata or legacy single ``screwType``). */
export function resolveAvailableScrewTypesForRow(row: VanDerValkSuitabilityConfig): string[] {
  const fromMeta = row.availableScrewTypes
  if (Array.isArray(fromMeta) && fromMeta.length > 0) {
    return fromMeta.map((s) => String(s).trim()).filter(Boolean)
  }
  const single = nonEmptyString(row.screwType)
  return single ? [single] : []
}

const ROOF_HOOK_BOM_SCREW_TYPE_CLADDINGS = new Set<VanDerValkPitchedCladdingType>(['MEMBRANE', 'SLATE'])

const ROOF_HOOK_BOM_EXPLICIT_SCREW_HOOK_TYPES = new Set(['STRONG', 'STRONG_HEAVY', 'SMART'])

/** True when the vendor BOM frame should carry a single ``screwType`` (not ``availableScrewTypes``). */
export function roofHookBomUsesExplicitScrewType(
  claddingType: VanDerValkPitchedCladdingType,
  roofHookType: string
): boolean {
  if (ROOF_HOOK_BOM_SCREW_TYPE_CLADDINGS.has(claddingType)) {
    return true
  }
  return ROOF_HOOK_BOM_EXPLICIT_SCREW_HOOK_TYPES.has(roofHookType.toUpperCase())
}

/** Single ``screwType`` for roof-hook BOM frames that require it (membrane, slate). */
export function resolveRoofHookBomScrewType(row: VanDerValkSuitabilityConfig): string | null {
  const explicit = nonEmptyString(row.screwType)
  if (explicit) {
    return explicit
  }
  const options = resolveAvailableScrewTypesForRow(row)
  return options[0] ?? null
}

function buildRoofHookFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string },
  claddingType: VanDerValkPitchedCladdingType
): PitchedVendorFrame | null {
  const systemType = nonEmptyString(row.system)
  if (!ROOF_HOOK_SYSTEMS.has(systemType)) {
    return null
  }

  const roofHookType = resolvePitchedRoofHookType(claddingType, row.roofHookType)
  if (!roofHookType) {
    return null
  }

  const longProfiles = resolveLongProfiles(row)
  if (!longProfiles.length) {
    return null
  }

  const frame: PitchedVendorFrame = {
    systemType,
    orientation: base.orientation,
    color: base.color,
    roofHookType,
    longProfiles,
    restrictPlacementToSubstructure: false,
  }

  if (screwTypeRequiredForRoofHook(roofHookType)) {
    if (roofHookBomUsesExplicitScrewType(claddingType, roofHookType)) {
      const screwType = resolveRoofHookBomScrewType(row)
      if (!screwType) {
        return null
      }
      frame.screwType = screwType
    } else {
      const availableScrewTypes = resolveAvailableScrewTypesForRow(row)
      if (!availableScrewTypes.length) {
        return null
      }
      frame.availableScrewTypes = availableScrewTypes
    }
  }

  return frame
}

/** MEMBRANE + ``VALK_SOLAR_FIX``: vendor frame (no ``systemType``). */
function buildMembraneSolarFixFrame(
  row: VanDerValkSuitabilityConfig,
  base: { orientation: string; color: string }
): PitchedVendorFrame | null {
  const longProfileType = nonEmptyString(row.longProfileType)
  const longProfiles = resolveLongProfiles(row)
  const maxCtcDistance = resolveMaxCtcDistanceForBom(row.maxCtcDistance)
  const anchorType = nonEmptyString(row.anchorType)

  if (!longProfileType || !longProfiles.length || maxCtcDistance == null || !anchorType) {
    return null
  }

  const frame: PitchedVendorFrame = {
    orientation: base.orientation,
    color: base.color,
    fixation: 'VALK_SOLAR_FIX',
    longProfileType,
    longProfiles,
    maxCtcDistance,
    anchorType,
  }

  if (anchorType === 'VALK_SOLAR_FIX') {
    const fastenerType = nonEmptyString(row.fastenerType)
    const roofMaterial = nonEmptyString(row.roofMaterial)
    const roofConstructionHeight = parsePositiveNumber(row.roofConstructionHeight)
    if (!fastenerType || !roofMaterial || roofConstructionHeight == null) {
      return null
    }
    if (!isValidSolarFixRoofConstructionHeight(roofMaterial, fastenerType, roofConstructionHeight)) {
      return null
    }
    frame.fastenerType = fastenerType
    frame.roofMaterial = roofMaterial
    frame.roofConstructionHeight = roofConstructionHeight
  }

  return frame
}

/**
 * Build vendor ``fields[*].frame`` for pitched-roof BOM from suitability row + roof-derived cladding type.
 */
export function buildPitchedVendorFrame(
  row: VanDerValkSuitabilityConfig | null | undefined,
  claddingType: VanDerValkPitchedCladdingType
): PitchedVendorFrame | null {
  if (row == null || !isVanDerValkSuitabilityConfigRow(row)) {
    return null
  }

  const base = baseOrientationAndColor(row)
  if (!base) {
    return null
  }

  const fixation = nonEmptyString(row.fixation)

  if (claddingType === 'STEEL_DECK') {
    if (fixation !== 'SCREWS' && fixation !== 'RIVET') {
      return null
    }
    return buildSimpleFixationFrame(row, base, fixation)
  }

  if (claddingType === 'CORRUGATED') {
    if (fixation === 'SCREWS' || fixation === 'RIVET') {
      return buildSimpleFixationFrame(row, base, fixation)
    }
    if (fixation === 'HANGER_BOLT') {
      return buildCorrugatedHangerBoltFrame(row, base)
    }
    if (fixation === 'MOUNTING_BRACKET') {
      return buildCorrugatedMountingBracketFrame(row, base)
    }
    return null
  }

  if (claddingType === 'STANDING_SEAM') {
    return buildStandingSeamFrame(row, base)
  }

  if (ROOF_HOOK_CLADDING_TYPES.has(claddingType)) {
    if (claddingType === 'MEMBRANE' && fixation === 'VALK_SOLAR_FIX') {
      return buildMembraneSolarFixFrame(row, base)
    }
    return buildRoofHookFrame(row, base, claddingType)
  }
  return null
}

function frameHasNonEmptyString(frame: PitchedVendorFrame, key: string): boolean {
  const v = frame[key]
  return typeof v === 'string' && v.trim() !== ''
}

function frameHasStringArray(frame: PitchedVendorFrame, key: string): boolean {
  const v = frame[key]
  return Array.isArray(v) && v.length > 0 && v.every((item) => typeof item === 'string' && item.trim() !== '')
}

function frameHasPositiveNumber(frame: PitchedVendorFrame, key: string): boolean {
  const v = frame[key]
  return typeof v === 'number' && Number.isFinite(v) && v > 0
}

/** True when the built frame satisfies vendor requirements for the cladding type. */
export function isPitchedVendorFrameComplete(
  claddingType: VanDerValkPitchedCladdingType,
  frame: PitchedVendorFrame | null | undefined,
  _claddingOptions?: PitchedCladdingOptions | null
): boolean {
  if (!frame) {
    return false
  }

  if (!frameHasNonEmptyString(frame, 'orientation') || !frameHasNonEmptyString(frame, 'color')) {
    return false
  }

  switch (claddingType) {
    case 'STEEL_DECK':
    case 'CORRUGATED':
      if (!frameHasNonEmptyString(frame, 'fixation')) {
        return false
      }
      if (frame.fixation === 'HANGER_BOLT') {
        return frameHasStringArray(frame, 'longProfiles') && frameHasNonEmptyString(frame, 'hangerBoltOption')
      }
      if (frame.fixation === 'MOUNTING_BRACKET') {
        return (
          frame.orientation === 'LANDSCAPE' &&
          frameHasStringArray(frame, 'longProfiles') &&
          frameHasNonEmptyString(frame, 'mountingBracketBoltSize')
        )
      }
      return frame.fixation === 'SCREWS' || frame.fixation === 'RIVET'

    case 'STANDING_SEAM':
      return (
        !('systemType' in frame) &&
        !('fixation' in frame) &&
        frameHasStringArray(frame, 'longProfiles') &&
        frameHasNonEmptyString(frame, 'seamClampType')
      )

    case 'TILED':
    case 'SLATE':
    case 'PLAIN':
    case 'MEMBRANE': {
      if (frameHasNonEmptyString(frame, 'fixation') && frame.fixation === 'VALK_SOLAR_FIX') {
        if (!frameHasNonEmptyString(frame, 'longProfileType') || !frameHasStringArray(frame, 'longProfiles')) {
          return false
        }
        if (!frameHasPositiveNumber(frame, 'maxCtcDistance') || !frameHasNonEmptyString(frame, 'anchorType')) {
          return false
        }
        if (frame.anchorType === 'OTHER') {
          return true
        }
        if (frame.anchorType !== 'VALK_SOLAR_FIX') {
          return false
        }
        if (
          !frameHasNonEmptyString(frame, 'fastenerType') ||
          !frameHasNonEmptyString(frame, 'roofMaterial') ||
          !frameHasPositiveNumber(frame, 'roofConstructionHeight')
        ) {
          return false
        }
        return isValidSolarFixRoofConstructionHeight(
          String(frame.roofMaterial),
          String(frame.fastenerType),
          Number(frame.roofConstructionHeight)
        )
      }
      if (!frameHasNonEmptyString(frame, 'systemType') || !frameHasNonEmptyString(frame, 'roofHookType')) {
        return false
      }
      if (!frameHasStringArray(frame, 'longProfiles')) {
        return false
      }
      if (typeof frame.restrictPlacementToSubstructure !== 'boolean') {
        return false
      }
      const hook = String(frame.roofHookType)
      if (screwTypeRequiredForRoofHook(hook)) {
        if (roofHookBomUsesExplicitScrewType(claddingType, hook)) {
          if (!frameHasNonEmptyString(frame, 'screwType')) {
            return false
          }
        } else if (!frameHasStringArray(frame, 'availableScrewTypes')) {
          return false
        }
      }
      if (claddingType === 'SLATE' && hook !== 'SLATE') {
        return false
      }
      if (claddingType === 'PLAIN' && hook !== 'PLAIN') {
        return false
      }
      if (claddingType === 'MEMBRANE' && hook !== 'BITUMEN') {
        return false
      }
      return true
    }

    default:
      return false
  }
}
